/** Player and media URLs resolved from a stored video link. */
export interface ResolvedVideoUrls {
    /** URL of a player for the video, used for third-party embeds (YouTube, Vimeo). */
    embedUrl?: string;
    /** URL of the media file itself, used when the link points straight at a video file. */
    contentUrl?: string;
}

const YOUTUBE_ID =
    /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?(?:[^#]*&)?v=|embed\/|shorts\/|v\/))([\w-]+)/i;
const VIMEO_ID =
    /vimeo\.com\/(?:video\/|channels\/[\w-]+\/|groups\/[\w-]+\/videos\/)?(\d+)/i;
const MEDIA_FILE_EXTENSION = /\.(?:mp4|webm|ogv|ogg|mov|m4v)(?:$|[?#])/i;

/**
 * Split a stored video link into the `embedUrl` / `contentUrl` pair schema.org expects.
 *
 * Videos are stored as whatever link the professional pasted: a YouTube watch link, a Vimeo page, or
 * occasionally a direct media file. Google requires at least one of `contentUrl` (the media file) or
 * `embedUrl` (a player), and explicitly rejects the page a video is watched on as `contentUrl`, so
 * third-party links are normalised to their canonical player URL and only real media files become
 * `contentUrl`. An unrecognised link is treated as a player, which is the safer of the two.
 */
export const resolveVideoUrls = (
    url: string | null | undefined,
): ResolvedVideoUrls => {
    const trimmed = url?.trim();

    if (!trimmed) {
        return {};
    }

    const youtubeID = YOUTUBE_ID.exec(trimmed)?.[1];

    if (youtubeID) {
        return { embedUrl: `https://www.youtube.com/embed/${youtubeID}` };
    }

    const vimeoID = VIMEO_ID.exec(trimmed)?.[1];

    if (vimeoID) {
        return { embedUrl: `https://player.vimeo.com/video/${vimeoID}` };
    }

    if (MEDIA_FILE_EXTENSION.test(trimmed)) {
        return { contentUrl: trimmed };
    }

    return { embedUrl: trimmed };
};
